<template>
  <div class="px-2">
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h1>Configuración del Sistema</h1>
      <router-link to="/billing" class="btn btn-primary">
        <i class="fas fa-sliders-h me-1" aria-hidden="true"></i>
        {{ $t('System Features') || 'System Features' }}
      </router-link>
    </div>

    <form @submit.prevent="saveSettings" class="d-flex flex-column ">
      <!-- Configuración Básica -->
      <div class="card" :class="{ '': !expandedSections.basicSettings }">
        <h3 class="section-title card-header bg-light" @click="toggleSection('basicSettings')">
          <span class="section-toggle">
            <i class="fas" :class="expandedSections.basicSettings ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
          </span>
          <i class="fas fa-cog me-2"></i>
          Configuración Básica
        </h3>

        <div class="card-body" v-show="expandedSections.basicSettings">
        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label for="siteName">Nombre del Sitio</label>
              <input
                type="text"
                id="siteName"
                v-model="settings.siteName"
                required
                class="form-control"
                :disabled="loading"
              />
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group">
              <label for="domain">Dominio</label>
              <input
                type="text"
                id="domain"
                v-model="settings.domain"
                class="form-control"
                :disabled="loading"
              />
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label for="systemCurrency">Moneda del Sistema</label>
              <select
                id="systemCurrency"
                v-model="settings.systemCurrency"
                class="form-control"
                :disabled="loading || loadingCurrencies"
                @change="onSystemCurrencyChange"
              >
                <option :value="null">Selecciona la moneda del sistema</option>
                <option 
                  v-for="currency in currencyOptions" 
                  :key="currency._id" 
                  :value="currency._id"
                >
                  {{ currency.label }}
                </option>
              </select>
              <small class="form-text text-muted">
                La moneda base para todos los cálculos y visualizaciones
              </small>
            </div>
          </div>
          <div class="col-md-6">
            <div class="form-group">
              <label for="numberFormat">Formato de Números</label>
              <select
                id="numberFormat"
                v-model="settings.numberFormat"
                class="form-control"
                :disabled="loading"
              >
                <option 
                  v-for="option in numberFormatOptions" 
                  :key="option.value" 
                  :value="option.value"
                >
                  {{ option.label }}
                </option>
              </select>
              <small class="form-text text-muted">
                Formato para mostrar números en el sistema
              </small>
            </div>
          </div>
        </div>

        <!-- Tasas de Conversión -->
        <div v-if="currencyOptions.length > 0 && settings.systemCurrency" class="currency-rates-section">
          <h4 class="subsection-title">
            <i class="fas fa-exchange-alt me-2"></i>
            Tasas de Conversión
          </h4>
          <p class="text-muted small mb-3">
            Las tasas se guardan con fecha efectiva. Al mostrar valores, se usa la tasa vigente según la fecha del documento.
          </p>
          <div class="currency-rates-list">
            <div 
              v-for="currency in currencyOptions" 
              :key="currency._id"
              class="currency-rate-item"
              :class="{ 'system-currency': currency._id === settings.systemCurrency }"
            >
              <div class="rate-equation">
                <span class="rate-base">1 {{ getSystemCurrencyLabel() }}</span>
                <span class="rate-equals">=</span>
                <template v-if="currency._id === settings.systemCurrency">
                  <span class="rate-value-fixed">1</span>
                </template>
                <template v-else>
                  <span class="rate-current-value">
                    {{ getCurrentRateDisplay(currency._id) || '—' }}
                  </span>
                </template>
                <span class="rate-currency">{{ currency.label }}</span>
              </div>
              <template v-if="currency._id === settings.systemCurrency">
                <span class="badge bg-success">Moneda del Sistema</span>
              </template>
              <template v-else>
                <span v-if="getCurrentRateDate(currency._id)" class="rate-date text-muted small">
                  Desde: {{ formatDate(getCurrentRateDate(currency._id)) }}
                </span>
                <div class="rate-actions">
                  <button 
                    type="button" 
                    class="btn btn-sm btn-outline-primary"
                    @click="openAddRateModal(currency)"
                    title="Agregar nueva tasa"
                  >
                    <i class="fas fa-plus"></i>
                  </button>
                  <button 
                    type="button" 
                    class="btn btn-sm btn-outline-secondary"
                    @click="openHistoryModal(currency)"
                    title="Ver historial"
                  >
                    <i class="fas fa-history"></i>
                  </button>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- Modal para agregar/editar tasa -->
        <div v-if="rateModal.show" class="modal-overlay" @click.self="closeRateModal">
          <div class="rate-modal">
            <div class="modal-header-custom">
              <h5 class="modal-title">
                <i class="fas fa-plus-circle me-2"></i>
                {{ rateModal.isEditing ? 'Editar' : 'Nueva' }} Tasa de Conversión
              </h5>
              <button type="button" class="btn-close-modal" @click="closeRateModal">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <div class="modal-body-custom">
              <p class="currency-info">
                <strong>{{ rateModal.currencyLabel }}</strong>
              </p>
              
              <div class="rate-input-group mb-3">
                <label class="form-label">Tasa de conversión</label>
                <div class="rate-equation-input">
                  <span class="rate-base">1 {{ getSystemCurrencyLabel() }}</span>
                  <span class="rate-equals">=</span>
                  <input
                    type="number"
                    step="0.0001"
                    min="0"
                    v-model.number="rateModal.displayValue"
                    class="form-control rate-input"
                    placeholder="0"
                  />
                  <span class="rate-currency">{{ rateModal.currencyLabel }}</span>
                </div>
                <small class="text-muted">
                  Ingresa cuántas unidades de {{ rateModal.currencyLabel }} equivalen a 1 {{ getSystemCurrencyLabel() }}
                </small>
              </div>

              <div class="form-group mb-3">
                <label class="form-label">Fecha efectiva</label>
                <input
                  type="datetime-local"
                  v-model="rateModal.effectiveDate"
                  class="form-control"
                />
                <small class="text-muted">
                  Esta tasa se aplicará a documentos creados a partir de esta fecha
                </small>
              </div>

              <div class="form-group mb-3">
                <label class="form-label">Notas (opcional)</label>
                <input
                  type="text"
                  v-model="rateModal.notes"
                  class="form-control"
                  placeholder="Ej: Ajuste por fluctuación"
                />
              </div>
            </div>
            <div class="modal-footer-custom">
              <button type="button" class="btn btn-secondary" @click="closeRateModal">
                Cancelar
              </button>
              <button 
                type="button" 
                class="btn btn-primary"
                @click="saveRate"
                :disabled="savingRate || !rateModal.displayValue"
              >
                <i v-if="savingRate" class="fas fa-spinner fa-spin me-1"></i>
                {{ savingRate ? 'Guardando...' : 'Guardar' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Modal de historial -->
        <div v-if="historyModal.show" class="modal-overlay" @click.self="closeHistoryModal">
          <div class="history-modal">
            <div class="modal-header-custom">
              <h5 class="modal-title">
                <i class="fas fa-history me-2"></i>
                Historial de Tasas: {{ historyModal.currencyLabel }}
              </h5>
              <button type="button" class="btn-close-modal" @click="closeHistoryModal">
                <i class="fas fa-times"></i>
              </button>
            </div>
            <div class="modal-body-custom">
              <ContentSkeleton
                v-if="loadingHistory"
                variant="list"
                :rows="5"
                padded
              />
              <div v-else-if="historyModal.records.length === 0" class="text-center py-4 text-muted">
                <i class="fas fa-info-circle fa-2x mb-2"></i>
                <p class="mb-0">No hay registros de tasas para esta moneda</p>
              </div>
              <div v-else class="history-list">
                <div 
                  v-for="record in historyModal.records" 
                  :key="record._id"
                  class="history-item"
                >
                  <div class="history-rate">
                    <strong>1 {{ record.systemCurrencyLabel }}</strong> = <strong>{{ record.displayValue }}</strong> {{ record.currencyLabel }}
                  </div>
                  <div class="history-meta">
                    <span class="history-date">
                      <i class="fas fa-calendar-alt me-1"></i>
                      {{ formatDateTime(record.effectiveDate) }}
                    </span>
                    <span v-if="record.notes" class="history-notes">
                      <i class="fas fa-sticky-note me-1"></i>
                      {{ record.notes }}
                    </span>
                  </div>
                  <div class="history-actions">
                    <button 
                      type="button" 
                      class="btn btn-sm btn-outline-primary"
                      @click="editRate(record)"
                      title="Editar"
                    >
                      <i class="fas fa-edit"></i>
                    </button>
                    <button 
                      type="button" 
                      class="btn btn-sm btn-outline-danger"
                      @click="deleteRate(record)"
                      title="Eliminar"
                    >
                      <i class="fas fa-trash"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div class="modal-footer-custom">
              <button type="button" class="btn btn-secondary" @click="closeHistoryModal">
                Cerrar
              </button>
              <button 
                type="button" 
                class="btn btn-primary"
                @click="openAddRateModal({ _id: historyModal.currencyId, label: historyModal.currencyLabel })"
              >
                <i class="fas fa-plus me-1"></i>
                Agregar Nueva Tasa
              </button>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label for="timezone">Zona Horaria</label>
              <select
                id="timezone"
                v-model="settings.timezone"
                class="form-control"
                :disabled="loading"
              >
                <option value="">Selecciona tu zona horaria</option>
                <option value="-12">
                  UTC-12 (Línea Internacional de Fecha Oeste)
                </option>
                <option value="-11">UTC-11 (Samoa, Midway)</option>
                <option value="-10">UTC-10 (Hawaii, Tahití)</option>
                <option value="-9">UTC-9 (Alaska, Hawái)</option>
                <option value="-8">UTC-8 (Pacífico, Los Ángeles)</option>
                <option value="-7">UTC-7 (Montaña, Denver)</option>
                <option value="-6">UTC-6 (Central, Chicago)</option>
                <option value="-5">UTC-5 (Este, Nueva York)</option>
                <option value="-4">UTC-4 (Atlántico, Caracas)</option>
                <option value="-3">UTC-3 (Brasilia, Buenos Aires)</option>
                <option value="-2">UTC-2 (Atlántico Medio)</option>
                <option value="-1">UTC-1 (Azores, Cabo Verde)</option>
                <option value="0">UTC+0 (Londres, Lisboa)</option>
                <option value="+1">UTC+1 (París, Berlín, Madrid)</option>
                <option value="+2">UTC+2 (Kiev, Helsinki, Atenas)</option>
                <option value="+3">UTC+3 (Moscú, Estambul, Riad)</option>
                <option value="+4">UTC+4 (Bakú, Tbilisi, Dubái)</option>
                <option value="+5">UTC+5 (Tashkent, Karachi)</option>
                <option value="+5.5">UTC+5:30 (Nueva Delhi, Mumbai)</option>
                <option value="+6">UTC+6 (Dhaka, Almaty)</option>
                <option value="+7">UTC+7 (Bangkok, Hanoi, Jakarta)</option>
                <option value="+8">UTC+8 (Pekín, Hong Kong, Singapur)</option>
                <option value="+9">UTC+9 (Tokio, Seúl, Pyongyang)</option>
                <option value="+10">
                  UTC+10 (Sídney, Melbourne, Brisbane)
                </option>
                <option value="+11">UTC+11 (Vladivostok, Islas Salomón)</option>
                <option value="+12">UTC+12 (Nueva Zelanda, Kamchatka)</option>
              </select>
              <small class="form-text text-muted"
                >Selecciona tu zona horaria para que las fechas se guarden
                correctamente</small
              >
            </div>
            </div>
          <div class="col-md-6">
            <div class="form-group">
              <label for="accountingStartDate">Inicio de la contabilidad</label>
              <input
                id="accountingStartDate"
                v-model="settings.accountingStartDate"
                type="date"
                class="form-control"
                :disabled="loading"
              />
              <small class="form-text text-muted">
                Lo anterior a esta fecha no genera asientos contables. Vacío: se contabiliza todo.
              </small>
            </div>
          </div>
          </div>
        </div>
      </div>

      <!-- Registro público (/register) -->
      <div class="card" :class="{ '': !expandedSections.register }">
        <h3 class="section-title card-header bg-light" @click="toggleSection('register')">
          <span class="section-toggle">
            <i class="fas" :class="expandedSections.register ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
          </span>
          <i class="fas fa-user-plus me-2"></i>
          Register and login
        </h3>

        <div class="card-body" v-show="expandedSections.register">
          <div class="row">
            <div class="col-md-12">
              <div class="form-check mb-3">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="logoutOnBrowserClose"
                  v-model="settings.logoutOnBrowserClose"
                  :disabled="loading"
                />
                <label class="form-check-label" for="logoutOnBrowserClose">
                  Cerrar sesión al cerrar el navegador
                </label>
                <small class="form-text text-muted d-block">
                  Al cerrar la última pestaña de la app (o el navegador entero) hay que volver a iniciar sesión. Cerrar una pestaña con otras abiertas o recargar no cierra la sesión.
                </small>
                <div v-show="settings.logoutOnBrowserClose" class="d-flex align-items-center flex-wrap gap-2 mt-2">
                  <label class="mb-0" for="logoutOnBrowserCloseWait">Tiempo de espera</label>
                  <input
                    id="logoutOnBrowserCloseWait"
                    type="number"
                    class="form-control form-control-sm"
                    style="width: 6rem"
                    :min="settings.logoutOnBrowserCloseWaitUnit === 'hours' ? 1 : 2"
                    step="1"
                    v-model.number="settings.logoutOnBrowserCloseWaitValue"
                    :disabled="loading"
                  />
                  <select
                    class="form-control form-control-sm"
                    style="width: auto"
                    v-model="settings.logoutOnBrowserCloseWaitUnit"
                    :disabled="loading"
                  >
                    <option value="minutes">minutos</option>
                    <option value="hours">horas</option>
                  </select>
                  <small class="form-text text-muted d-block w-100">
                    Si vuelven a abrir la app antes de este tiempo, la sesión sigue abierta. Mínimo 2 minutos.
                  </small>
                </div>
              </div>
              <div class="form-check mb-3">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="registerEnabled"
                  v-model="settings.registerEnabled"
                  :disabled="loading"
                  @change="onRegisterEnabledChange"
                />
                <label class="form-check-label" for="registerEnabled">
                  Activar register
                </label>
                <small class="form-text text-muted d-block">
                  Si está desactivado, la ruta pública <code>/register</code> no estará disponible y el registro público quedará bloqueado.
                </small>
              </div>
              <div v-show="settings.registerEnabled" class="ms-2 ps-3 border-start border-secondary mb-0">
                <div class="form-group mb-3">
                  <label for="registerDefaultRole">Rol por defecto (cuentas desde /register)</label>
                  <select
                    id="registerDefaultRole"
                    v-model="settings.registerDefaultRole"
                    class="form-control"
                    :disabled="loading || !registerRoleChoices.length"
                  >
                    <option
                      v-for="r in registerRoleChoices"
                      :key="r._id"
                      :value="r.name"
                    >
                      {{ r.name }} — {{ r.description }}
                    </option>
                  </select>
                  <small class="form-text text-muted d-block">
                    No se listan roles de administrador. El servidor asigna siempre este rol; el cliente no puede elegir otro.
                  </small>
                </div>
                <div class="form-check mb-3">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="registerRequireEmailConfirmation"
                    v-model="settings.registerRequireEmailConfirmation"
                    :disabled="loading"
                  />
                  <label class="form-check-label" for="registerRequireEmailConfirmation">
                    Exigir confirmación por email (registro público /register)
                  </label>
                  <small class="form-text text-muted d-block">
                    Si está marcado, todo alta desde <code>/register</code> recibe un enlace y <strong>no puede iniciar sesión</strong> hasta confirmar. Con <strong>Register new App</strong>, la copia de la BD dedicada se hace al confirmar; si Register new App está desactivado, solo se activa la cuenta en esta instancia al confirmar.
                  </small>
                </div>
                <div class="form-check mb-2">
                  <input
                    class="form-check-input"
                    type="checkbox"
                    id="registerNewApp"
                    v-model="settings.registerNewApp"
                    :disabled="loading"
                    @change="onRegisterNewAppChange"
                  />
                  <label class="form-check-label" for="registerNewApp">
                    Register new App
                  </label>
                  <small class="form-text text-muted d-block">
                    Si está activo, cada registro público crea también una base de datos exclusiva (copia de la plantilla) con el nombre derivado del negocio del usuario, y el mismo usuario queda en esta instancia. En la copia se reutiliza el usuario de plantilla que elija abajo (solo se cambian su email y contraseña por los del registro; no se crea otro usuario en esa BD).
                  </small>
                </div>
                <div v-show="settings.registerNewApp" class="form-group mb-3">
                  <label for="registerTemplateDbName">Nombre BD plantilla (Mongo) a copiar</label>
                  <input
                    id="registerTemplateDbName"
                    v-model="settings.registerTemplateDbName"
                    type="text"
                    class="form-control"
                    placeholder="ej. mi_plantilla_exportai"
                    autocomplete="off"
                    :disabled="loading"
                    @input="onRegisterTemplateDbNameInput"
                  />
                  <small class="form-text text-muted">
                    Solo letras, números y guiones bajos. Debe existir en el mismo servidor Mongo (<code>MONGO_URI</code>). No se envía al cliente público.
                  </small>
                </div>
                <div v-show="settings.registerNewApp" class="form-group mb-0">
                  <label for="registerTemplateReplaceUserEmail">Usuario de la plantilla a reutilizar en la copia</label>
                  <div class="d-flex flex-wrap gap-2 align-items-start mb-2">
                    <select
                      id="registerTemplateReplaceUserEmail"
                      v-model="settings.registerTemplateReplaceUserEmail"
                      class="form-control flex-grow-1"
                      style="min-width: 220px"
                      :disabled="loading || registerTemplateUsersLoading"
                    >
                      <option value="">— Cargue la lista y elija un email —</option>
                      <option
                        v-for="u in registerTemplateUsers"
                        :key="u.email"
                        :value="u.email"
                      >
                        {{ u.name ? `${u.name} · ` : '' }}{{ u.email }}
                      </option>
                    </select>
                    <button
                      type="button"
                      class="btn btn-outline-secondary btn-sm text-nowrap"
                      :disabled="loading || registerTemplateUsersLoading || !registerTemplateDbNameTrimmed"
                      @click="loadRegisterTemplateUsers"
                    >
                      <i
                        class="fas me-1"
                        :class="registerTemplateUsersLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'"
                      />
                      Cargar usuarios
                    </button>
                  </div>
                  <small class="form-text text-muted">
                    Tras clonar la plantilla, en la nueva BD solo se actualizan el <strong>email</strong> y la <strong>contraseña</strong> de este usuario (el resto de datos y referencias quedan como en la plantilla). Debe existir en la BD plantilla con ese email.
                  </small>
                </div>

                <div class="form-group mt-3 mb-0">
                  <label for="registerTemplateConfigUserEmail">
                    Usuario modelo para heredar configuración (My menu y columnas)
                  </label>
                  <div class="d-flex flex-wrap gap-2 align-items-start mb-2">
                    <select
                      id="registerTemplateConfigUserEmail"
                      v-model="settings.registerTemplateConfigUserEmail"
                      class="form-control flex-grow-1"
                      style="min-width: 220px"
                      :disabled="loading || registerConfigTemplateUsersLoading"
                    >
                      <option value="">— Opcional: no heredar configuración —</option>
                      <option
                        v-for="u in registerConfigTemplateUsers"
                        :key="u.email"
                        :value="u.email"
                      >
                        {{ u.name ? `${u.name} · ` : '' }}{{ u.email }}
                      </option>
                    </select>
                    <button
                      type="button"
                      class="btn btn-outline-secondary btn-sm text-nowrap"
                      :disabled="loading || registerConfigTemplateUsersLoading"
                      @click="loadRegisterConfigTemplateUsers"
                    >
                      <i
                        class="fas me-1"
                        :class="registerConfigTemplateUsersLoading ? 'fa-spinner fa-spin' : 'fa-sync-alt'"
                      />
                      Cargar usuarios
                    </button>
                  </div>
                  <small class="form-text text-muted">
                    Si se selecciona, cada nuevo usuario de <code>/register</code> copiará desde este usuario modelo: <strong>My menu</strong>, preferencias de <strong>columnas</strong>, <strong>filtros</strong>, <strong>calendario</strong> y el <strong>idioma del perfil</strong>.
                  </small>
                </div>

                <hr class="my-3" />
                <h5 class="text-secondary mb-3">
                  <i class="fas fa-envelope me-2" />
                  Emails de registro (app dedicada)
                </h5>
                <p class="small text-muted mb-3">
                  Shortcodes (copie tal cual en el HTML):
                  <span
                    v-pre
                    class="d-inline-block font-monospace small ms-1"
                  >{{name}} {{email}} {{password}} {{token}} {{confirmLink}} {{frontendUrl}} {{businessName}} {{businessSlug}} {{tenantUrl}} {{companyLink}} {{country}} {{siteName}} {{defaultRole}} {{templateDbName}}</span>.
                  La contraseña en texto claro solo se guarda temporalmente hasta confirmar el email (riesgo si alguien accede a la base de datos).
                </p>
                <div class="form-group mb-2">
                  <label for="registerEmailFrontendBaseUrl">URL base del front (enlaces de confirmación)</label>
                  <input
                    id="registerEmailFrontendBaseUrl"
                    v-model="settings.registerEmailFrontendBaseUrl"
                    type="url"
                    class="form-control"
                    placeholder="Vacío = variable de entorno FRONTEND_URL"
                    autocomplete="off"
                    :disabled="loading"
                  />
                </div>
                <div class="form-group mb-3">
                  <label for="registerTenantUrlPattern">Patrón URL del negocio (se reemplaza solo <span v-pre class="font-monospace">{{businessSlug}}</span>)</label>
                  <input
                    id="registerTenantUrlPattern"
                    v-model="settings.registerTenantUrlPattern"
                    type="text"
                    class="form-control"
                    :placeholder="registerTenantUrlPlaceholder"
                    autocomplete="off"
                    :disabled="loading"
                  />
                </div>
                <div class="row">
                  <div class="col-lg-6 mb-3">
                    <label for="registerConfirmationEmailSubject">1 — Asunto: email con enlace de confirmación</label>
                    <input
                      id="registerConfirmationEmailSubject"
                      v-model="settings.registerConfirmationEmailSubject"
                      type="text"
                      class="form-control mb-2"
                      placeholder="Vacío = asunto por defecto del sistema"
                      :disabled="loading"
                    />
                    <label for="registerConfirmationEmailHtml" class="d-block">HTML</label>
                    <textarea
                      id="registerConfirmationEmailHtml"
                      v-model="settings.registerConfirmationEmailHtml"
                      class="form-control font-monospace small"
                      rows="10"
                      placeholder="Vacío = plantilla HTML por defecto"
                      :disabled="loading"
                    />
                  </div>
                  <div class="col-lg-6 mb-3">
                    <label for="registerWelcomeEmailSubject">2 — Asunto: email tras confirmar la cuenta</label>
                    <input
                      id="registerWelcomeEmailSubject"
                      v-model="settings.registerWelcomeEmailSubject"
                      type="text"
                      class="form-control mb-2"
                      placeholder="Vacío = asunto por defecto del sistema"
                      :disabled="loading"
                    />
                    <label for="registerWelcomeEmailHtml" class="d-block">HTML</label>
                    <textarea
                      id="registerWelcomeEmailHtml"
                      v-model="settings.registerWelcomeEmailHtml"
                      class="form-control font-monospace small"
                      rows="10"
                      placeholder="Vacío = plantilla HTML por defecto"
                      :disabled="loading"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm mb-0"
                  :disabled="loading"
                  @click="restoreRegisterEmailDefaults"
                >
                  <i class="fas fa-undo me-1" />
                  Restaurar textos por defecto (solo en pantalla; guarde para persistir)
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Información de la Empresa -->
      <div class="card" :class="{ '': !expandedSections.companyInfo }">
        <h3 class="section-title card-header bg-light" @click="toggleSection('companyInfo')">
          <span class="section-toggle">
            <i class="fas" :class="expandedSections.companyInfo ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
          </span>
          <i class="fas fa-building me-2"></i>
          Información de la Empresa
        </h3>

        <div class="card-body" v-show="expandedSections.companyInfo">
        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label for="companyName">Nombre de la Empresa</label>
              <input
                type="text"
                id="companyName"
                v-model="settings.companyName"
                class="form-control"
                :disabled="loading"
                placeholder="Ingresa el nombre de la empresa"
              />
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group">
              <label for="companyEmail">Email</label>
              <input
                type="email"
                id="companyEmail"
                v-model="settings.companyEmail"
                class="form-control"
                :disabled="loading"
                placeholder="Ingresa el email de la empresa"
              />
          </div>
        </div>
      </div>

        <div class="row">
          <div class="col-md-6">
            <div class="form-group">
              <label for="companyWebsite">Sitio Web</label>
                  <input
                type="text"
                id="companyWebsite"
                v-model="settings.companyWebsite"
                    class="form-control"
                    :disabled="loading"
                placeholder="https://www.ejemplo.com"
              />
            </div>
          </div>

          <div class="col-md-6">
            <div class="form-group">
              <label for="companyAddress">Dirección</label>
              <textarea
                id="companyAddress"
                v-model="settings.companyAddress"
                class="form-control"
                :disabled="loading"
                rows="3"
                placeholder="Ingresa la dirección de la empresa"
              ></textarea>
            </div>
                </div>
              </div>
            </div>
          </div>

      <!-- Branding -->
      <div class="card" :class="{ '': !expandedSections.branding }">
        <h3 class="section-title card-header bg-light" @click="toggleSection('branding')">
          <span class="section-toggle">
            <i class="fas" :class="expandedSections.branding ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
          </span>
          <i class="fas fa-palette me-2"></i>
          Branding
        </h3>

        <div class="card-body" v-show="expandedSections.branding">
        <div class="row">
          <div class="col-md-4">
            <div class="mb-3">
              <label for="logo" class="form-label">Logo</label>
              <div class="d-flex flex-column gap-2">
                <div v-if="logoPreview" class="position-relative logo-preview">
                  <img :src="logoPreview" alt="Vista previa del logo" class="img-fluid rounded border" />
                  <button type="button" class="btn btn-danger btn-sm remove-btn" @click.stop.prevent="removeLogo">
                    <i class="material-icons">close</i>
                  </button>
                </div>
                <input type="file" id="logo" ref="logoInput" accept="image/*" @change="handleLogoUpload" class="form-control" :disabled="loading" />
                <small class="form-text text-muted">Formatos permitidos: JPG, PNG, GIF. Tamaño máximo: 2MB</small>
                </div>
            </div>
          </div>

          <div class="col-md-4">
            <div class="mb-3">
              <label for="menuBackgroundImage" class="form-label">Fondo del Menú</label>
              <div class="d-flex flex-column gap-2">
                <div v-if="menuBackgroundPreview" class="position-relative logo-preview background-preview">
                  <img :src="menuBackgroundPreview" alt="Vista previa del fondo del menú" class="img-fluid rounded border" style="object-fit: cover;" />
                  <button type="button" class="btn btn-danger btn-sm remove-btn" @click.stop.prevent="removeMenuBackground">
                    <i class="material-icons">close</i>
                  </button>
                </div>
                <input type="file" id="menuBackgroundImage" ref="menuBackgroundInput" accept="image/*" @change="handleMenuBackgroundUpload" class="form-control" :disabled="loading" />
                <small class="form-text text-muted">Imagen de fondo para el menú lateral</small>
              </div>
            </div>
          </div>

          <div class="col-md-4">
            <div class="mb-3">
              <label for="loginBackgroundImage" class="form-label">Fondo del Login</label>
              <div class="d-flex flex-column gap-2">
                <div v-if="loginBackgroundPreview" class="position-relative logo-preview background-preview">
                  <img :src="loginBackgroundPreview" alt="Vista previa del fondo del login" class="img-fluid rounded border" style="object-fit: cover;" />
                  <button type="button" class="btn btn-danger btn-sm remove-btn" @click.stop.prevent="removeLoginBackground">
                    <i class="material-icons">close</i>
                  </button>
                </div>
                <input type="file" id="loginBackgroundImage" ref="loginBackgroundInput" accept="image/*" @change="handleLoginBackgroundUpload" class="form-control" :disabled="loading" />
                <small class="form-text text-muted">Imagen de fondo para la página de login</small>
                </div>
              </div>
            </div>
          </div>
          <div class="row mt-3">
            <div class="col-md-4">
              <div class="mb-3">
                <label for="favicon" class="form-label">Favicon</label>
                <div class="d-flex flex-column gap-2">
                  <div v-if="faviconPreview" class="position-relative logo-preview favicon-preview">
                    <img :src="faviconPreview" alt="Vista previa del favicon" class="rounded border" style="max-width: 32px; max-height: 32px; object-fit: contain;" />
                    <button type="button" class="btn btn-danger btn-sm remove-btn" @click.stop.prevent="removeFavicon">
                      <i class="material-icons">close</i>
                    </button>
                  </div>
                  <input type="file" id="favicon" ref="faviconInput" accept="image/x-icon,image/png,image/gif,image/svg+xml" @change="handleFaviconUpload" class="form-control" :disabled="loading" />
                  <small class="form-text text-muted">Icono de la pestaña del navegador. ICO, PNG, GIF o SVG. Recomendado: 32×32 px.</small>
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="mb-3">
                <label for="primaryColor" class="form-label">Color primario del sistema</label>
                <div class="d-flex align-items-center gap-2 flex-wrap">
                  <input
                    type="color"
                    id="primaryColor"
                    v-model="settings.primaryColor"
                    class="form-control form-control-color p-1"
                    style="width: 3rem; height: 2.25rem; cursor: pointer;"
                    :disabled="loading"
                  />
                  <input
                    type="text"
                    v-model="settings.primaryColor"
                    class="form-control"
                    style="max-width: 8rem; font-family: monospace;"
                    placeholder="#11b5c9"
                    :disabled="loading"
                  />
                </div>
                <small class="form-text text-muted">Se aplica a botones, enlaces, iconos y elementos destacados en toda la aplicación.</small>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- API Keys -->
      <div class="card" :class="{ '': !expandedSections.apiKeys }">
        <h3 class="section-title card-header bg-light" @click="toggleSection('apiKeys')">
          <span class="section-toggle">
            <i class="fas" :class="expandedSections.apiKeys ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
          </span>
          <i class="fas fa-key me-2"></i>
          API Keys
        </h3>

        <div class="card-body" v-show="expandedSections.apiKeys">
          <div class="row">
            <div class="col-md-6">
              <div class="form-group">
                <label for="llmProvider">Proveedor de IA</label>
                <select
                  id="llmProvider"
                  v-model="settings.llmProvider"
                  class="form-select"
                  :disabled="loading"
                >
                  <option value="deepseek">DeepSeek</option>
                  <option value="openai">OpenAI</option>
                </select>
                <small class="form-text text-muted"
                  >Define qué API se usa en soporte, análisis, MEV, jurisprudencia y demás módulos con IA.</small
                >
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label for="deepseekApiKey">API Key de DeepSeek</label>
                <input
                  type="password"
                  id="deepseekApiKey"
                  v-model="settings.deepseekApiKey"
                  class="form-control"
                  :disabled="loading"
                  placeholder="sk-… (DeepSeek)"
                />
                <small class="form-text text-muted"
                  >Necesaria si el proveedor activo es DeepSeek (o para cambiar después sin reingresar la otra clave).</small
                >
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label for="openaiApiKey">API Key de OpenAI</label>
                <input
                  type="password"
                  id="openaiApiKey"
                  v-model="settings.openaiApiKey"
                  class="form-control"
                  :disabled="loading"
                  placeholder="sk-… (OpenAI)"
                />
                <small class="form-text text-muted"
                  >Necesaria si el proveedor activo es OpenAI. Modelos por defecto: gpt-4o-mini (rápido) y gpt-4o (complejo).</small
                >
              </div>
            </div>
            <div class="col-md-6">
              <div class="form-group">
                <label for="sendinblueApiKey">API Key Brevo (Sendinblue)</label>
                <input
                  type="password"
                  id="sendinblueApiKey"
                  v-model="settings.sendinblueApiKey"
                  class="form-control"
                  :disabled="loading"
                  placeholder="xkeysib-… (desde el panel de Brevo)"
                />
                <small class="form-text text-muted"
                  >Correos transaccionales del sistema (registro, confirmación, recuperación de contraseña, etc.). Si queda vacío, se usa la variable <code>SENDINBLUE_API_KEY</code> del servidor (.env).</small
                >
              </div>
            </div>

            <div v-if="isAdmin" class="col-12 mt-3 pt-3 border-top">
              <h5 class="h6 mb-2">
                <i class="fas fa-plug me-2"></i>
                API externa — asistente IA
              </h5>
              <p class="small text-muted mb-2">
                Token para integraciones fuera del navegador (p. ej. Telegram). Incluye la base de datos de este tenant cifrada; la misma URL de API sirve para todos los clientes.
              </p>
              <div class="form-group mb-3">
                <label for="externalAiUserSelect">Usuario para peticiones API</label>
                <select
                  id="externalAiUserSelect"
                  class="form-select form-select-sm"
                  v-model="externalAiSelectedUserId"
                  :disabled="loading || externalAiTokenBusy"
                >
                  <option value="">— Elegí usuario —</option>
                  <option
                    v-for="u in externalAiUserOptions"
                    :key="u.id"
                    :value="u.id"
                  >
                    {{ u.email }}<template v-if="u.name"> · {{ u.name }}</template><template v-if="u.roleName"> ({{ u.roleName }})</template>
                  </option>
                </select>
                <small class="form-text text-muted">Creaciones y permisos del asistente usarán este usuario (p. ej. <code>createdBy</code>).</small>
              </div>
              <div
                v-if="settings.externalAiApiTokenConfigured && !generatedTokenOnce"
                class="alert alert-secondary small py-2 mb-2"
              >
                <div><strong>Token activo:</strong> <code>{{ settings.externalAiApiTokenPreview || '—' }}</code></div>
                <div
                  v-if="settings.externalAiApiUserEmail"
                  class="text-muted mt-1 small"
                >
                  Peticiones como: {{ settings.externalAiApiUserEmail }}<template v-if="settings.externalAiApiUserName"> — {{ settings.externalAiApiUserName }}</template>
                </div>
                <div v-if="settings.externalAiApiTokenGeneratedAt" class="text-muted mt-1">
                  Generado: {{ formatExternalAiTokenDate(settings.externalAiApiTokenGeneratedAt) }}
                </div>
              </div>
              <div v-if="generatedTokenOnce" class="form-group mb-2">
                <label for="externalAiTokenOnce">Token generado (cópialo ahora)</label>
                <div class="input-group">
                  <input
                    id="externalAiTokenOnce"
                    type="text"
                    class="form-control font-monospace small"
                    :value="generatedTokenOnce"
                    readonly
                  />
                  <button
                    type="button"
                    class="btn btn-outline-secondary"
                    :disabled="loading"
                    @click="copyExternalAiToken"
                  >
                    <i class="fas fa-copy"></i>
                  </button>
                </div>
                <small class="form-text text-muted">No se volverá a mostrar completo. Al generar uno nuevo, el anterior deja de funcionar.</small>
              </div>
              <div class="d-flex flex-wrap gap-2 mb-2">
                <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  :disabled="loading || externalAiTokenBusy || !externalAiSelectedUserId"
                  @click="generateExternalAiApiToken"
                >
                  <i class="fas fa-sync-alt me-1"></i>
                  {{ settings.externalAiApiTokenConfigured ? 'Generar nuevo token' : 'Generar token' }}
                </button>
                <button
                  v-if="settings.externalAiApiTokenConfigured"
                  type="button"
                  class="btn btn-outline-danger btn-sm"
                  :disabled="loading || externalAiTokenBusy"
                  @click="revokeExternalAiApiToken"
                >
                  Revocar token
                </button>
              </div>
              <details class="small text-muted">
                <summary class="mb-1" style="cursor: pointer;">Probar con curl</summary>
                <pre class="bg-light border rounded p-2 mb-0 small" style="white-space: pre-wrap;">curl -X POST "{{ externalAiChatUrl }}" \
  -H "Authorization: Bearer &lt;tu_token&gt;" \
  -H "Content-Type: application/json" \
  -d '{"messages":[{"role":"user","content":"Hola"}]}'</pre>
              </details>
            </div>
          </div>
        </div>
      </div>

      <!-- Parámetros Outlook (OAuth Azure) -->
      <div v-if="isAdmin" id="settings-outlook" class="card" :class="{ '': !expandedSections.outlook }">
        <h3 class="section-title card-header bg-light" @click="toggleSection('outlook')">
          <span class="section-toggle">
            <i class="fas" :class="expandedSections.outlook ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
          </span>
          <i class="fab fa-microsoft me-2" aria-hidden="true"></i>
          Outlook
        </h3>
        <div class="card-body" v-show="expandedSections.outlook">
          <outlook-settings embedded />
        </div>
      </div>

      <!-- Parámetros de Telegram (bot / webhook; no aplica al chat de soporte web) -->
      <div id="settings-telegram" class="card" :class="{ '': !expandedSections.telegram }">
        <h3 class="section-title card-header bg-light" @click="toggleSection('telegram')">
          <span class="section-toggle">
            <i class="fas" :class="expandedSections.telegram ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
          </span>
          <i class="fab fa-telegram-plane me-2" aria-hidden="true"></i>
          {{ $t("settings.telegramParametersTab") }}
        </h3>
        <div class="card-body" v-show="expandedSections.telegram">
          <telegram-ai-settings embedded />
        </div>
      </div>

      <!-- Gestión de Base de Datos (Solo Admin) -->
      <div v-if="isAdmin" class="card" :class="{ '': !expandedSections.database }">
        <h3 class="section-title card-header bg-light" @click="toggleSection('database')">
          <span class="section-toggle">
            <i class="fas" :class="expandedSections.database ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
          </span>
          <i class="fas fa-database me-2"></i>
          Gestión de Base de Datos
        </h3>
        <div class="card-body" v-show="expandedSections.database">
        <div class="row g-4">
          <div class="col-md-4">
            <div class="p-3 bg-white rounded border h-100">
            <h4 class="h5">Exportar Base de Datos</h4>
            <p class="text-muted small">
              Selecciona qué componentes del sistema deseas exportar a un archivo JSON
            </p>
            <div class="p-3 bg-light rounded border mb-3">
              <div class="form-check mb-2">
                <input class="form-check-input" type="checkbox" v-model="exportOptions.models" :disabled="loading" id="chkModels" />
                <label class="form-check-label" for="chkModels">Modelos y definiciones del sistema</label>
              </div>
              <div class="form-check mb-2 border-top pt-2">
                <input class="form-check-input" type="checkbox" v-model="selectAllLists" :disabled="loading" @change="toggleAllLists" id="chkAllLists" />
                <label class="form-check-label fw-bold" for="chkAllLists">Listados (seleccionar todos)</label>
                <div class="ms-4 mt-2 overflow-auto bg-white border rounded p-2" style="max-height: 200px;" v-if="availableListTypes.length > 0">
                  <div class="form-check" v-for="listType in availableListTypes" :key="listType.type">
                    <input class="form-check-input" type="checkbox" v-model="exportOptions.listTypes" :value="listType.type" :disabled="loading" :id="'chk_' + listType.type" />
                    <label class="form-check-label" :for="'chk_' + listType.type">{{ listType.name }} ({{ listType.type }})</label>
                  </div>
                </div>
                <div v-else class="text-muted small ms-4 mt-2">Cargando tipos de listados...</div>
              </div>
              <div class="form-check mb-2">
                <input class="form-check-input" type="checkbox" v-model="exportOptions.views" :disabled="loading" id="chkViews" />
                <label class="form-check-label" for="chkViews">Vistas</label>
              </div>
              <div class="form-check mb-2">
                <input class="form-check-input" type="checkbox" v-model="exportOptions.referenceMappings" :disabled="loading" id="chkRefMappings" />
                <label class="form-check-label" for="chkRefMappings">Mapeos de referencias</label>
              </div>
              <div class="form-check mb-2">
                <input class="form-check-input" type="checkbox" v-model="exportOptions.adminLinks" :disabled="loading" id="chkAdminLinks" />
                <label class="form-check-label" for="chkAdminLinks">Enlaces administrativos</label>
              </div>
              <div class="form-check mb-2">
                <input class="form-check-input" type="checkbox" v-model="exportOptions.relationships" :disabled="loading" id="chkRelationships" />
                <label class="form-check-label" for="chkRelationships">Configuraciones de relaciones</label>
              </div>
              <div class="form-check mb-2">
                <input class="form-check-input" type="checkbox" v-model="exportOptions.layouts" :disabled="loading" id="chkLayouts" />
                <label class="form-check-label" for="chkLayouts">Layouts y posiciones de diagramas</label>
              </div>
              <div class="form-check mb-2">
                <input class="form-check-input" type="checkbox" v-model="exportOptions.queryReports" :disabled="loading" id="chkQueryReports" />
                <label class="form-check-label" for="chkQueryReports">Reportes de consultas</label>
              </div>
              <div class="form-check">
                <input class="form-check-input" type="checkbox" v-model="exportOptions.importMappings" :disabled="loading" id="chkImportMappings" />
                <label class="form-check-label" for="chkImportMappings">Mapeos de JSON Import</label>
              </div>
            </div>
            <button type="button" class="btn btn-success" @click="exportDatabase" :disabled="loading || !hasExportOptionsSelected">
              <i class="fas fa-download me-2"></i>
              {{ loading ? "Exportando..." : "Exportar Base de Datos" }}
            </button>
            </div>
          </div>

          <div class="col-md-4">
            <div class="p-3 bg-white rounded border h-100">
            <h4 class="h5">Importar Base de Datos</h4>
            <p class="text-muted small">
              Importa las tablas específicas desde un archivo JSON a la base de datos actual
            </p>
            <div class="alert alert-warning py-2 small">
              <strong>⚠️ Importante:</strong> Esta acción sobrescribirá los datos existentes.<br />
              Se importarán modelos, relaciones, layouts y configuraciones del sistema.
            </div>
            <div class="mb-3">
              <label for="importFile" class="form-label">Archivo JSON</label>
              <input type="file" id="importFile" ref="importFileInput" accept=".json" @change="handleImportFile" class="form-control" :disabled="loading" />
              <small class="form-text text-muted">Selecciona un archivo JSON exportado previamente</small>
            </div>
            <button type="button" class="btn btn-warning" @click="importDatabase" :disabled="!importFile || loading">
              <i class="fas fa-upload me-2"></i>
              {{ loading ? "Importando..." : "Importar Base de Datos" }}
            </button>
            </div>
          </div>

          <div class="col-md-4">
            <DatabaseCopy />
          </div>
          </div>
        </div>
      </div>

      <!-- Términos de Servicio -->
      <div class="card" :class="{ '': !expandedSections.termsOfService }">
        <h3 class="section-title card-header bg-light" @click="toggleSection('termsOfService')">
          <span class="section-toggle">
            <i class="fas" :class="expandedSections.termsOfService ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
          </span>
          <i class="fas fa-file-contract me-2"></i>
          Términos de Servicio
        </h3>

        <div class="card-body" v-show="expandedSections.termsOfService">
        <div class="row">
          <div class="col-md-12 mb-3">
            <div class="form-group">
              <label for="termsOfServiceVersion">Versión de los Términos</label>
              <input
                type="text"
                id="termsOfServiceVersion"
                v-model="settings.termsOfServiceVersion"
                class="form-control"
                :disabled="loading"
                placeholder="Ej: 1.0, 2.0, etc."
              />
              <small class="form-text text-muted">
                Cambiar la versión forzará a todos los usuarios a aceptar los nuevos términos al iniciar sesión
              </small>
            </div>
          </div>
          <div class="col-md-12">
            <div class="form-group">
              <label for="termsOfServiceText">Texto de los Términos de Servicio</label>
              <div class="mb-2">
                <small class="form-text text-muted">
                  Puedes copiar y pegar texto con formato. El formato se preservará cuando los usuarios vean los términos.
                </small>
              </div>
              <div 
                ref="termsEditor"
                contenteditable="true"
                id="termsOfServiceText"
                class="form-control terms-editor"
                :style="{ minHeight: '400px', padding: '12px', whiteSpace: 'pre-wrap', overflowWrap: 'break-word' }"
                @input="onTermsTextChange"
                @paste="onTermsPaste"
                v-html="settings.termsOfServiceText"
              ></div>
              <small class="form-text text-muted">
                El texto se mostrará exactamente como lo escribes, preservando saltos de línea y formato básico
              </small>
            </div>
          </div>
        </div>
      </div>
      </div>

      <!-- Código Personalizado -->
      <div class="card" :class="{ '': !expandedSections.customCode }">
        <h3 class="section-title card-header bg-light" @click="toggleSection('customCode')">
          <span class="section-toggle">
            <i class="fas" :class="expandedSections.customCode ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
          </span>
          <i class="fas fa-code me-2"></i>
          Código Personalizado
        </h3>

        <div class="card-body" v-show="expandedSections.customCode">
        <div class="row">
          <div class="col-md-6">
            <div class="mb-3">
              <label for="customCss" class="form-label">Código CSS Personalizado</label>
              <textarea id="customCss" v-model="settings.customCss" class="form-control code-textarea" :disabled="loading" placeholder="Ingresa tu código CSS personalizado aquí..." rows="10"></textarea>
              <small class="form-text text-muted">Puedes agregar estilos CSS personalizados para tu sitio</small>
            </div>
          </div>

          <div class="col-md-6">
            <div class="mb-3">
              <label for="customJavaScript" class="form-label">Código JavaScript Personalizado</label>
              <textarea id="customJavaScript" v-model="settings.customJavaScript" class="form-control code-textarea" :disabled="loading" placeholder="Ingresa tu código JavaScript personalizado aquí..." rows="10"></textarea>
              <small class="form-text text-muted">Puedes agregar código JavaScript personalizado para tu sitio</small>
            </div>
            </div>
          </div>
        </div>
      </div>

      <button type="submit" class="btn btn-primary" :disabled="loading">
        {{ loading ? "Guardando..." : "Guardar Configuración" }}
      </button>
    </form>
  </div>
</template>

<script>
import { wencesApi, getWencesApiBaseUrl } from "@/api/wencesApi";
import Swal from "sweetalert2";
import DatabaseCopy from "@/components/gotrading/common/DatabaseCopy.vue";
import TelegramAiSettings from "@/modules/telegram_ai_module/components/TelegramAiSettings.vue";
import OutlookSettings from "@/modules/outlook_module/components/OutlookSettings.vue";
import { setNumberFormatCache } from "@/utils/numberFormatter";
import {
  logoutWaitFromMinutes,
  logoutWaitToMinutes,
} from "@/utils/browserCloseLogout";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

export default {
  name: "Settings",
  components: {
    DatabaseCopy,
    TelegramAiSettings,
    OutlookSettings,
    ContentSkeleton,
  },
  data() {
    return {
      settings: {
        siteName: "",
        domain: "",
        timezone: "",
        logo: "",
        customCss: "",
        customJavaScript: "",
        searatesApiKey: "",
        llmProvider: "deepseek",
        deepseekApiKey: "",
        openaiApiKey: "",
        sendinblueApiKey: "",
        externalAiApiTokenConfigured: false,
        externalAiApiTokenPreview: "",
        externalAiApiTokenGeneratedAt: null,
        externalAiApiUserId: "",
        externalAiApiUserEmail: "",
        externalAiApiUserName: "",
        // Company information
        companyName: "",
        companyEmail: "",
        companyWebsite: "",
        companyAddress: "",
        // Background images
        menuBackgroundImage: "",
        loginBackgroundImage: "",
        favicon: "",
        // Primary color (hex, e.g. #11b5c9)
        primaryColor: "",
        // System currency
        systemCurrency: null,
        // Number format
        numberFormat: "dot.comma.2",
        accountingStartDate: "",
        // Terms of Service
        termsOfServiceText: "",
        termsOfServiceVersion: "1.0",
        logoutOnBrowserClose: false,
        logoutOnBrowserCloseWaitValue: 2,
        logoutOnBrowserCloseWaitUnit: "minutes",
        registerEnabled: false,
        registerNewApp: false,
        registerRequireEmailConfirmation: true,
        registerTemplateDbName: "",
        registerTemplateReplaceUserEmail: "",
        registerTemplateConfigUserEmail: "",
        registerEmailFrontendBaseUrl: "",
        registerTenantUrlPattern: "",
        registerConfirmationEmailSubject: "",
        registerConfirmationEmailHtml: "",
        registerWelcomeEmailSubject: "",
        registerWelcomeEmailHtml: "",
        registerDefaultRole: "restaurant",
      },
      registerTenantUrlPlaceholder: "https://{{businessSlug}}.iajur.com",
      registerTemplateUsers: [],
      registerTemplateUsersLoading: false,
      registerTemplateUsersLoadedFor: "",
      registerConfigTemplateUsers: [],
      registerConfigTemplateUsersLoading: false,
      registerRoleOptions: [],
      // Currency options
      currencyOptions: [],
      loadingCurrencies: false,
      currencyListId: null,
      // Currency rate history
      currentRates: [], // Tasas actuales cargadas del backend
      loadingRates: false,
      savingRate: false,
      loadingHistory: false,
      // Modal para agregar/editar tasa
      rateModal: {
        show: false,
        isEditing: false,
        recordId: null,
        currencyId: null,
        currencyLabel: '',
        displayValue: null,
        effectiveDate: '',
        notes: ''
      },
      // Modal de historial
      historyModal: {
        show: false,
        currencyId: null,
        currencyLabel: '',
        records: []
      },
      // Collapsed sections state
      expandedSections: {
        database: false,
        basicSettings: false,
        register: false,
        apiKeys: false,
        branding: false,
        companyInfo: false,
        billing: false,
        customCode: false,
        termsOfService: false,
        telegram: false,
        outlook: false,
      },
      logoFile: null,
      logoPreview: null,
      menuBackgroundFile: null,
      menuBackgroundPreview: null,
      loginBackgroundFile: null,
      loginBackgroundPreview: null,
      faviconFile: null,
      faviconPreview: null,
      loading: false,
      generatedTokenOnce: "",
      externalAiTokenBusy: false,
      externalAiUserOptions: [],
      externalAiSelectedUserId: "",
      // Datos para gestión de base de datos
      importFile: null,
      isAdmin: false,
      // Opciones de exportación
      exportOptions: {
        models: true,
        listTypes: [], // Array de tipos de listados seleccionados
        views: true,
        referenceMappings: true,
        adminLinks: true,
        relationships: true,
        layouts: true,
        queryReports: true,
        importMappings: true,
      },
      // Tipos de listados disponibles
      availableListTypes: [],
    };
  },
  watch: {
    "$route.query.tab"(tab) {
      this.expandTelegramSectionFromRoute(tab);
    },
    "$route.query.section"(section) {
      this.expandTelegramSectionFromRoute(section);
    },
  },
  async created() {
    this.expandTelegramSectionFromRoute(
      this.$route.query.tab || this.$route.query.section
    );
    await this.loadSettings();
    await this.loadCurrencyOptions();
    await this.loadCurrentRates();
    await this.checkAdminRole();
    if (this.isAdmin) {
      await this.loadListTypes();
      await this.loadRegisterRoles();
      await this.loadRegisterConfigTemplateUsers();
      await this.loadExternalAiApiUsers();
    }
  },
  methods: {
    expandTelegramSectionFromRoute(tab) {
      const t = String(tab || "").trim().toLowerCase();
      if (t !== "telegram") return;
      this.expandedSections.telegram = true;
      this.$nextTick(() => {
        const el = document.getElementById("settings-telegram");
        if (el && typeof el.scrollIntoView === "function") {
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      });
    },
    formatExternalAiTokenDate(value) {
      if (!value) return "";
      try {
        const d = new Date(value);
        if (Number.isNaN(d.getTime())) return String(value);
        return d.toLocaleString();
      } catch {
        return String(value);
      }
    },
    async loadExternalAiApiUsers() {
      if (!this.isAdmin) return;
      try {
        const token = localStorage.getItem("token");
        const res = await wencesApi.get("/api/settings/external-ai-token/users", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data && res.data.success && Array.isArray(res.data.data)) {
          this.externalAiUserOptions = res.data.data;
        }
      } catch {
        this.externalAiUserOptions = [];
      }
    },
    async generateExternalAiApiToken() {
      if (!this.isAdmin || this.externalAiTokenBusy) return;
      const uid = String(this.externalAiSelectedUserId || "").trim();
      if (!uid) {
        Swal.fire({
          title: "Usuario requerido",
          text: "Elegí qué usuario representará las peticiones hechas con este token.",
          icon: "warning",
        });
        return;
      }
      const replace = this.settings.externalAiApiTokenConfigured;
      if (replace) {
        const confirm = await Swal.fire({
          title: "¿Generar nuevo token?",
          text: "El token anterior dejará de funcionar de inmediato.",
          icon: "warning",
          showCancelButton: true,
          confirmButtonText: "Generar",
          cancelButtonText: "Cancelar",
        });
        if (!confirm.isConfirmed) return;
      }
      this.externalAiTokenBusy = true;
      try {
        const token = localStorage.getItem("token");
        const res = await wencesApi.post(
          "/api/settings/external-ai-token/generate",
          { externalAiApiUserId: uid },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data && res.data.success && res.data.data) {
          this.generatedTokenOnce = res.data.data.token || "";
          this.settings.externalAiApiUserId =
            res.data.data.externalAiApiUserId || uid;
          const pick = this.externalAiUserOptions.find((o) => o.id === uid);
          if (pick) {
            this.settings.externalAiApiUserEmail = pick.email || "";
            this.settings.externalAiApiUserName = pick.name || "";
          }
          this.settings.externalAiApiTokenConfigured = true;
          this.settings.externalAiApiTokenPreview =
            res.data.data.tokenPreview || "";
          this.settings.externalAiApiTokenGeneratedAt =
            res.data.data.generatedAt || null;
          await Swal.fire({
            title: "Token generado",
            text: res.data.message || "Copiá el token ahora; no se volverá a mostrar.",
            icon: "success",
          });
        } else {
          throw new Error(
            (res.data && (res.data.error || res.data.message)) ||
              "No se pudo generar el token"
          );
        }
      } catch (e) {
        const msg =
          e && e.response && e.response.data && e.response.data.error
            ? String(e.response.data.error)
            : e && e.message
              ? e.message
              : "Error al generar el token";
        Swal.fire({ title: "Error", text: msg, icon: "error" });
      } finally {
        this.externalAiTokenBusy = false;
      }
    },
    async revokeExternalAiApiToken() {
      if (!this.isAdmin || this.externalAiTokenBusy) return;
      const confirm = await Swal.fire({
        title: "¿Revocar token?",
        text: "Las integraciones externas dejarán de acceder hasta que generes uno nuevo.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Revocar",
        cancelButtonText: "Cancelar",
      });
      if (!confirm.isConfirmed) return;
      this.externalAiTokenBusy = true;
      try {
        const token = localStorage.getItem("token");
        const res = await wencesApi.post(
          "/api/settings/external-ai-token/revoke",
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data && res.data.success) {
          this.generatedTokenOnce = "";
          this.settings.externalAiApiTokenConfigured = false;
          this.settings.externalAiApiTokenPreview = "";
          this.settings.externalAiApiTokenGeneratedAt = null;
          this.settings.externalAiApiUserId = "";
          this.settings.externalAiApiUserEmail = "";
          this.settings.externalAiApiUserName = "";
          this.externalAiSelectedUserId = "";
          Swal.fire({ title: "Revocado", icon: "success", timer: 2000 });
        } else {
          throw new Error(
            (res.data && res.data.error) || "No se pudo revocar el token"
          );
        }
      } catch (e) {
        const msg =
          e && e.response && e.response.data && e.response.data.error
            ? String(e.response.data.error)
            : e && e.message
              ? e.message
              : "Error al revocar el token";
        Swal.fire({ title: "Error", text: msg, icon: "error" });
      } finally {
        this.externalAiTokenBusy = false;
      }
    },
    async copyExternalAiToken() {
      const text = String(this.generatedTokenOnce || "").trim();
      if (!text) return;
      try {
        await navigator.clipboard.writeText(text);
        Swal.fire({
          title: "Copiado",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        });
      } catch {
        Swal.fire({
          title: "No se pudo copiar",
          text: "Seleccioná el texto del campo y copialo manualmente.",
          icon: "info",
        });
      }
    },
    clearRegisterTemplateUserPick() {
      this.settings.registerTemplateReplaceUserEmail = "";
      this.registerTemplateUsers = [];
      this.registerTemplateUsersLoadedFor = "";
    },
    onRegisterTemplateDbNameInput() {
      const t = this.registerTemplateDbNameTrimmed;
      if (t !== this.registerTemplateUsersLoadedFor) {
        this.clearRegisterTemplateUserPick();
      }
    },
    restoreRegisterEmailDefaults() {
      this.settings.registerConfirmationEmailSubject = "Confirma tu cuenta";
      this.settings.registerConfirmationEmailHtml = `<h1>Hola {{name}},</h1>
<p>Gracias por registrarte. Para activar tu cuenta y crear tu aplicación dedicada, haz clic en el siguiente enlace:</p>
<p><a href="{{confirmLink}}">Confirmar cuenta</a></p>
<p>Si el botón no funciona, copia y pega esta URL en el navegador:</p>
<p><code>{{confirmLink}}</code></p>
<p>Atentamente,<br/>{{siteName}}</p>`;
      this.settings.registerWelcomeEmailSubject = "Tu cuenta está confirmada";
      this.settings.registerWelcomeEmailHtml = `<h1>Bienvenido/a, {{name}}</h1>
<p>Tu cuenta quedó confirmada y la base de datos de tu negocio está lista.</p>
<p><strong>Identificador (BD / URL):</strong> {{businessSlug}}</p>
<p><strong>Acceso sugerido:</strong> <a href="{{tenantUrl}}">{{tenantUrl}}</a></p>
<p><strong>Email de acceso:</strong> {{email}}</p>
<p><strong>Contraseña elegida:</strong> {{password}}</p>
<p>Atentamente,<br/>{{siteName}}</p>`;
    },
    async loadRegisterTemplateUsers() {
      const dbName = this.registerTemplateDbNameTrimmed;
      if (!dbName || !/^[a-zA-Z0-9_]+$/.test(dbName)) {
        Swal.fire({
          title: "Nombre inválido",
          text: "Indique un nombre de base plantilla válido (solo letras, números y guión bajo).",
          icon: "warning",
        });
        return;
      }
      this.registerTemplateUsersLoading = true;
      try {
        const token = localStorage.getItem("token");
        const { data } = await wencesApi.get("/api/settings/register-template-users", {
          params: { templateDbName: dbName },
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = data?.data;
        this.registerTemplateUsers = Array.isArray(list) ? list : [];
        this.registerTemplateUsersLoadedFor = dbName;
        if (
          this.settings.registerTemplateReplaceUserEmail &&
          !this.registerTemplateUsers.some(
            (u) => u.email === this.settings.registerTemplateReplaceUserEmail
          )
        ) {
          this.settings.registerTemplateReplaceUserEmail = "";
        }
        if (!this.registerTemplateUsers.length) {
          Swal.fire({
            title: "Sin usuarios",
            text: "No hay usuarios elegibles en esa plantilla (o la base está vacía). Los administradores no se listan.",
            icon: "info",
          });
        }
      } catch (e) {
        this.registerTemplateUsers = [];
        this.registerTemplateUsersLoadedFor = "";
        Swal.fire({
          title: "Error",
          text: e.response?.data?.error || "No se pudo cargar la lista de usuarios.",
          icon: "error",
        });
      } finally {
        this.registerTemplateUsersLoading = false;
      }
    },
    async loadRegisterConfigTemplateUsers() {
      this.registerConfigTemplateUsersLoading = true;
      try {
        const token = localStorage.getItem("token");
        const { data } = await wencesApi.get("/api/settings/register-config-template-users", {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = data?.data;
        this.registerConfigTemplateUsers = Array.isArray(list) ? list : [];
        if (
          this.settings.registerTemplateConfigUserEmail &&
          !this.registerConfigTemplateUsers.some(
            (u) => u.email === this.settings.registerTemplateConfigUserEmail
          )
        ) {
          this.settings.registerTemplateConfigUserEmail = "";
        }
      } catch (e) {
        this.registerConfigTemplateUsers = [];
        Swal.fire({
          title: "Error",
          text: e.response?.data?.error || "No se pudo cargar la lista de usuarios modelo.",
          icon: "error",
        });
      } finally {
        this.registerConfigTemplateUsersLoading = false;
      }
    },
    onRegisterNewAppChange() {
      if (!this.settings.registerNewApp) {
        this.settings.registerTemplateDbName = "";
        this.clearRegisterTemplateUserPick();
      }
    },
    onRegisterEnabledChange() {
      if (!this.settings.registerEnabled) {
        this.settings.registerNewApp = false;
        this.settings.registerTemplateDbName = "";
        this.clearRegisterTemplateUserPick();
      } else if (this.isAdmin && !this.registerRoleOptions.length) {
        this.loadRegisterRoles();
      }
    },
    async loadRegisterRoles() {
      if (!this.isAdmin) return;
      try {
        const response = await wencesApi.get("/api/roles");
        const list = response.data?.data || [];
        this.registerRoleOptions = Array.isArray(list) ? list : [];
        this.ensureRegisterDefaultRoleValid();
      } catch (e) {
        this.registerRoleOptions = [];
      }
    },
    ensureRegisterDefaultRoleValid() {
      const names = this.registerRoleChoices.map((r) => r.name);
      if (!names.length) return;
      if (!this.settings.registerDefaultRole || !names.includes(this.settings.registerDefaultRole)) {
        this.settings.registerDefaultRole = names[0];
      }
    },
    formatNumberExample(number, formatString) {
      // formatString: "thousandsSeparator.decimalSeparator.decimals"
      const [thousandsSep, decimalSep, decimals] = formatString.split('.');
      
      const decimalsCount = parseInt(decimals);
      // Use 1000 as base number
      const numValue = 1000;
      
      // Format with decimals
      const fixed = numValue.toFixed(decimalsCount);
      const parts = fixed.split('.');
      let integerPart = parts[0];
      let decimalPart = parts[1] || '';
      
      // Pad decimal part if needed
      if (decimalsCount > 0 && decimalPart.length < decimalsCount) {
        decimalPart = decimalPart.padEnd(decimalsCount, '0');
      }
      
      // Add thousands separator
      if (thousandsSep === 'dot') {
        integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
      } else if (thousandsSep === 'comma') {
        integerPart = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      }
      
      // Combine with decimal separator
      if (decimalsCount > 0) {
        const decimalSymbol = decimalSep === 'dot' ? '.' : ',';
        return `${integerPart}${decimalSymbol}${decimalPart}`;
      } else {
        return integerPart;
      }
    },
    toggleSection(section) {
      this.expandedSections[section] = !this.expandedSections[section];
    },
    onTermsTextChange(event) {
      // Actualizar el modelo cuando el usuario escribe
      this.settings.termsOfServiceText = event.target.innerHTML || event.target.innerText;
    },
    onTermsPaste(event) {
      // Permitir pegar texto preservando formato básico
      event.preventDefault();
      const text = (event.clipboardData || window.clipboardData).getData('text/html') || 
                   (event.clipboardData || window.clipboardData).getData('text/plain');
      
      // Insertar el texto en el editor
      const selection = window.getSelection();
      if (selection.rangeCount > 0) {
        const range = selection.getRangeAt(0);
        range.deleteContents();
        
        // Crear un elemento temporal para procesar el HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = text;
        
        // Insertar el contenido preservando formato básico
        const fragment = document.createDocumentFragment();
        while (tempDiv.firstChild) {
          fragment.appendChild(tempDiv.firstChild);
        }
        range.insertNode(fragment);
        
        // Mover el cursor al final
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
      }
      
      // Actualizar el modelo
      this.$nextTick(() => {
        if (this.$refs.termsEditor) {
          this.settings.termsOfServiceText = this.$refs.termsEditor.innerHTML;
        }
      });
    },
    async loadSettings() {
      this.loading = true;
      try {
        const token = localStorage.getItem("token");
        const response = await wencesApi.get("/api/settings", {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (response.data.success && response.data.data) {
          const settingsData = response.data.data;
          this.settings = {
            siteName: settingsData.siteName || "",
            domain: settingsData.domain || "",
            timezone: settingsData.timezone || this.detectUserTimezone(),
            logo: settingsData.logo || "",
            customCss: settingsData.customCss || "",
            customJavaScript: settingsData.customJavaScript || "",
            searatesApiKey: settingsData.searatesApiKey || "",
            llmProvider: settingsData.llmProvider === "openai" ? "openai" : "deepseek",
            deepseekApiKey: settingsData.deepseekApiKey || "",
            openaiApiKey: settingsData.openaiApiKey || "",
            sendinblueApiKey: settingsData.sendinblueApiKey || "",
            // Company information
            companyName: settingsData.companyName || "",
            companyEmail: settingsData.companyEmail || "",
            companyWebsite: settingsData.companyWebsite || "",
            companyAddress: settingsData.companyAddress || "",
            // Background images
            menuBackgroundImage: settingsData.menuBackgroundImage || "",
            loginBackgroundImage: settingsData.loginBackgroundImage || "",
            favicon: settingsData.favicon || "",
            // Primary color
            primaryColor: settingsData.primaryColor || "",
            // System currency
            systemCurrency: settingsData.systemCurrency || null,
            // Number format
            numberFormat: settingsData.numberFormat || "dot.comma.2",
            accountingStartDate: settingsData.accountingStartDate
              ? String(settingsData.accountingStartDate).slice(0, 10)
              : "",
            // Terms of Service
            termsOfServiceText: settingsData.termsOfServiceText || "",
            termsOfServiceVersion: settingsData.termsOfServiceVersion || "1.0",
            logoutOnBrowserClose: settingsData.logoutOnBrowserClose === true,
            ...logoutWaitFromMinutes(settingsData.logoutOnBrowserCloseMinutes),
            registerEnabled: settingsData.registerEnabled === true,
            registerNewApp: settingsData.registerNewApp === true,
            registerRequireEmailConfirmation:
              settingsData.registerRequireEmailConfirmation !== false,
            registerTemplateDbName: settingsData.registerTemplateDbName || "",
            registerTemplateReplaceUserEmail:
              settingsData.registerTemplateReplaceUserEmail || "",
            registerTemplateConfigUserEmail:
              settingsData.registerTemplateConfigUserEmail || "",
            registerEmailFrontendBaseUrl: settingsData.registerEmailFrontendBaseUrl || "",
            registerTenantUrlPattern: settingsData.registerTenantUrlPattern || "",
            registerConfirmationEmailSubject:
              settingsData.registerConfirmationEmailSubject || "",
            registerConfirmationEmailHtml: settingsData.registerConfirmationEmailHtml || "",
            registerWelcomeEmailSubject: settingsData.registerWelcomeEmailSubject || "",
            registerWelcomeEmailHtml: settingsData.registerWelcomeEmailHtml || "",
            registerDefaultRole: settingsData.registerDefaultRole || "restaurant",
            externalAiApiTokenConfigured:
              settingsData.externalAiApiTokenConfigured === true,
            externalAiApiTokenPreview: settingsData.externalAiApiTokenPreview || "",
            externalAiApiTokenGeneratedAt:
              settingsData.externalAiApiTokenGeneratedAt || null,
            externalAiApiUserId: settingsData.externalAiApiUserId
              ? String(settingsData.externalAiApiUserId)
              : "",
            externalAiApiUserEmail: settingsData.externalAiApiUserEmail || "",
            externalAiApiUserName: settingsData.externalAiApiUserName || "",
          };
          this.generatedTokenOnce = "";
          this.externalAiSelectedUserId =
            this.settings.externalAiApiUserId || "";
          if (
            this.settings.registerNewApp &&
            this.settings.registerTemplateReplaceUserEmail &&
            this.settings.registerTemplateDbName
          ) {
            this.registerTemplateUsersLoadedFor = "";
            this.registerTemplateUsers = [
              {
                email: this.settings.registerTemplateReplaceUserEmail,
                name: "",
              },
            ];
            this.registerTemplateUsersLoadedFor =
              this.registerTemplateDbNameTrimmed || "";
          }
          if (this.settings.logo) {
            this.logoPreview = this.settings.logo;
          }
          if (this.settings.menuBackgroundImage) {
            this.menuBackgroundPreview = this.settings.menuBackgroundImage;
          }
          if (this.settings.loginBackgroundImage) {
            this.loginBackgroundPreview = this.settings.loginBackgroundImage;
          }
          if (this.settings.favicon) {
            this.faviconPreview = this.settings.favicon;
          }

          // Guardar la zona horaria en localStorage para uso en otros componentes
          if (this.settings.timezone) {
            localStorage.setItem("userTimezone", this.settings.timezone);
          }
          
          // Guardar la moneda del sistema en localStorage (se actualizará cuando se carguen las currencies)
          if (this.settings.systemCurrency) {
            localStorage.setItem("systemCurrencyId", this.settings.systemCurrency);
          }

          // Guardar el formato de números en localStorage
          if (this.settings.numberFormat) {
            localStorage.setItem("numberFormat", this.settings.numberFormat);
            setNumberFormatCache(this.settings.numberFormat);
          }
        } else {
          throw new Error(
            response.data.error || "Error al cargar la configuración"
          );
        }
      } catch (error) {
        console.error("Error loading settings:", error);
        Swal.fire({
          title: "Error",
          text:
            error.response?.data?.error || "Error al cargar la configuración",
          icon: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    handleLogoUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      const validTypes = ["image/jpeg", "image/png", "image/gif"];
      if (!validTypes.includes(file.type)) {
        Swal.fire({
          title: "Error",
          text: "Por favor, selecciona una imagen válida (JPG, PNG o GIF)",
          icon: "error",
        });
        this.$refs.logoInput.value = "";
        return;
      }
      const maxSize = 2 * 1024 * 1024;
      if (file.size > maxSize) {
        Swal.fire({
          title: "Error",
          text: "La imagen no debe superar los 2MB",
          icon: "error",
        });
        this.$refs.logoInput.value = "";
        return;
      }
      this.logoFile = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.logoPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    removeLogo() {
      this.logoFile = null;
      this.logoPreview = null;
      this.settings.logo = "";
      this.$refs.logoInput.value = "";
    },
    handleFaviconUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      const validTypes = ["image/x-icon", "image/png", "image/gif", "image/svg+xml"];
      if (!validTypes.includes(file.type)) {
        Swal.fire({
          title: "Error",
          text: "Formato no válido. Usa ICO, PNG, GIF o SVG.",
          icon: "error",
        });
        this.$refs.faviconInput.value = "";
        return;
      }
      const maxSize = 512 * 1024; // 512KB for favicon
      if (file.size > maxSize) {
        Swal.fire({
          title: "Error",
          text: "El favicon no debe superar los 512 KB",
          icon: "error",
        });
        this.$refs.faviconInput.value = "";
        return;
      }
      this.faviconFile = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.faviconPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    removeFavicon() {
      this.faviconFile = null;
      this.faviconPreview = null;
      this.settings.favicon = "";
      if (this.$refs.faviconInput) this.$refs.faviconInput.value = "";
    },
    handleMenuBackgroundUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
      if (!validTypes.includes(file.type)) {
        Swal.fire({
          title: "Error",
          text: "Por favor, selecciona una imagen válida (JPG, PNG, GIF o WEBP)",
          icon: "error",
        });
        this.$refs.menuBackgroundInput.value = "";
        return;
      }
      const maxSize = 5 * 1024 * 1024; // 5MB for background images
      if (file.size > maxSize) {
        Swal.fire({
          title: "Error",
          text: "La imagen no debe superar los 5MB",
          icon: "error",
        });
        this.$refs.menuBackgroundInput.value = "";
        return;
      }
      this.menuBackgroundFile = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.menuBackgroundPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    removeMenuBackground() {
      this.menuBackgroundFile = null;
      this.menuBackgroundPreview = null;
      this.settings.menuBackgroundImage = "";
      this.$refs.menuBackgroundInput.value = "";
    },
    handleLoginBackgroundUpload(event) {
      const file = event.target.files[0];
      if (!file) return;
      const validTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
      if (!validTypes.includes(file.type)) {
        Swal.fire({
          title: "Error",
          text: "Por favor, selecciona una imagen válida (JPG, PNG, GIF o WEBP)",
          icon: "error",
        });
        this.$refs.loginBackgroundInput.value = "";
        return;
      }
      const maxSize = 5 * 1024 * 1024; // 5MB for background images
      if (file.size > maxSize) {
        Swal.fire({
          title: "Error",
          text: "La imagen no debe superar los 5MB",
          icon: "error",
        });
        this.$refs.loginBackgroundInput.value = "";
        return;
      }
      this.loginBackgroundFile = file;
      const reader = new FileReader();
      reader.onload = (e) => {
        this.loginBackgroundPreview = e.target.result;
      };
      reader.readAsDataURL(file);
    },
    removeLoginBackground() {
      this.loginBackgroundFile = null;
      this.loginBackgroundPreview = null;
      this.settings.loginBackgroundImage = "";
      this.$refs.loginBackgroundInput.value = "";
    },
    async saveSettings() {
      this.loading = true;
      try {
        const token = localStorage.getItem("token");
        const settingsData = {
          siteName: this.settings.siteName,
          domain: this.settings.domain,
          timezone: this.settings.timezone,
          searatesApiKey: this.settings.searatesApiKey,
          llmProvider: this.settings.llmProvider === "openai" ? "openai" : "deepseek",
          deepseekApiKey: this.settings.deepseekApiKey,
          openaiApiKey: this.settings.openaiApiKey,
          sendinblueApiKey: this.settings.sendinblueApiKey,
          customCss: this.settings.customCss,
          customJavaScript: this.settings.customJavaScript,
          // Branding
          primaryColor: this.settings.primaryColor ? this.settings.primaryColor.trim() : "",
          favicon: this.settings.favicon || "",
          // Company information
          companyName: this.settings.companyName,
          companyEmail: this.settings.companyEmail,
          companyWebsite: this.settings.companyWebsite,
          companyAddress: this.settings.companyAddress,
          // System currency
          systemCurrency: this.settings.systemCurrency,
          // Number format
          numberFormat: this.settings.numberFormat,
          accountingStartDate: this.settings.accountingStartDate || null,
          // Terms of Service
          termsOfServiceText: this.settings.termsOfServiceText,
          termsOfServiceVersion: this.settings.termsOfServiceVersion,
          logoutOnBrowserClose: !!this.settings.logoutOnBrowserClose,
          logoutOnBrowserCloseMinutes: logoutWaitToMinutes(
            this.settings.logoutOnBrowserCloseWaitValue,
            this.settings.logoutOnBrowserCloseWaitUnit
          ),
          registerEnabled: !!this.settings.registerEnabled,
          registerNewApp: !!this.settings.registerNewApp,
          registerRequireEmailConfirmation: !!this.settings.registerRequireEmailConfirmation,
          registerTemplateDbName: (this.settings.registerTemplateDbName || "").trim(),
          registerTemplateReplaceUserEmail: (
            this.settings.registerTemplateReplaceUserEmail || ""
          ).trim(),
          registerTemplateConfigUserEmail: (
            this.settings.registerTemplateConfigUserEmail || ""
          ).trim(),
          registerEmailFrontendBaseUrl: (
            this.settings.registerEmailFrontendBaseUrl || ""
          ).trim(),
          registerTenantUrlPattern: (this.settings.registerTenantUrlPattern || "").trim(),
          registerConfirmationEmailSubject: (
            this.settings.registerConfirmationEmailSubject || ""
          ).trim(),
          registerConfirmationEmailHtml: this.settings.registerConfirmationEmailHtml || "",
          registerWelcomeEmailSubject: (this.settings.registerWelcomeEmailSubject || "").trim(),
          registerWelcomeEmailHtml: this.settings.registerWelcomeEmailHtml || "",
          registerDefaultRole: (this.settings.registerDefaultRole || "").trim() || "restaurant",
        };
        // Logo
        if (this.logoFile) {
          const reader = new FileReader();
          const base64Promise = new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
          });
          reader.readAsDataURL(this.logoFile);
          settingsData.logo = await base64Promise;
        } else if (this.settings.logo) {
          settingsData.logo = this.settings.logo;
        }
        // Menu Background Image
        if (this.menuBackgroundFile) {
          const reader = new FileReader();
          const base64Promise = new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
          });
          reader.readAsDataURL(this.menuBackgroundFile);
          settingsData.menuBackgroundImage = await base64Promise;
        } else {
          settingsData.menuBackgroundImage = this.settings.menuBackgroundImage;
        }
        // Login Background Image
        if (this.loginBackgroundFile) {
          const reader = new FileReader();
          const base64Promise = new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
          });
          reader.readAsDataURL(this.loginBackgroundFile);
          settingsData.loginBackgroundImage = await base64Promise;
        } else {
          settingsData.loginBackgroundImage = this.settings.loginBackgroundImage;
        }
        if (this.faviconFile) {
          const reader = new FileReader();
          const base64Promise = new Promise((resolve, reject) => {
            reader.onload = () => resolve(reader.result);
            reader.onerror = reject;
          });
          reader.readAsDataURL(this.faviconFile);
          settingsData.favicon = await base64Promise;
        } else {
          settingsData.favicon = this.settings.favicon || "";
        }
        await wencesApi.put("/api/settings", settingsData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        // Guardar la zona horaria en localStorage para uso en otros componentes
        if (this.settings.timezone) {
          localStorage.setItem("userTimezone", this.settings.timezone);
        }
        
        // Actualizar la moneda del sistema en localStorage
        this.updateSystemCurrencyInLocalStorage();

        // Actualizar el cache del formato de números
        if (this.settings.numberFormat) {
          setNumberFormatCache(this.settings.numberFormat);
        }

        this.$root.$emit("settings-saved");
        Swal.fire({
          title: "¡Éxito!",
          text: "Configuración guardada exitosamente",
          icon: "success",
          timer: 2000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Error saving settings:", error);
        let errorMessage = "Error al guardar la configuración";
        if (error.response && error.response.data && error.response.data.error)
          errorMessage = error.response.data.error;
        else if (error.message) errorMessage = error.message;
        Swal.fire({ title: "Error", text: errorMessage, icon: "error" });
      } finally {
        this.loading = false;
      }
    },

    // Admin only helpers
    async checkAdminRole() {
      const role = localStorage.getItem("role");
      this.isAdmin = role === "admin";
    },

    handleImportFile(event) {
      const file = event.target.files[0];
      if (!file) {
        this.importFile = null;
        return;
      }
      if (!file.name.endsWith(".json") && file.type !== "application/json") {
        Swal.fire({
          title: "Error",
          text: "Por favor, selecciona un archivo JSON válido",
          icon: "error",
        });
        this.$refs.importFileInput.value = "";
        this.importFile = null;
        return;
      }
      const maxSize = 50 * 1024 * 1024;
      if (file.size > maxSize) {
        Swal.fire({
          title: "Error",
          text: "El archivo no debe superar los 50MB",
          icon: "error",
        });
        this.$refs.importFileInput.value = "";
        this.importFile = null;
        return;
      }
      this.importFile = file;
    },
    async exportDatabase() {
      if (!this.hasExportOptionsSelected) {
        Swal.fire({
          title: "Atención",
          text: "Por favor, selecciona al menos una opción para exportar",
          icon: "warning",
        });
        return;
      }

      this.loading = true;
      try {
        const token = localStorage.getItem("token");
        
        // Construir query params basado en las opciones seleccionadas
        const params = new URLSearchParams();
        if (this.exportOptions.models) params.append("models", "true");
        // Agregar tipos de listados seleccionados
        this.exportOptions.listTypes.forEach((listType) => {
          params.append("listTypes", listType);
        });
        if (this.exportOptions.views) params.append("views", "true");
        if (this.exportOptions.referenceMappings) params.append("referenceMappings", "true");
        if (this.exportOptions.adminLinks) params.append("adminLinks", "true");
        if (this.exportOptions.relationships) params.append("relationships", "true");
        if (this.exportOptions.layouts) params.append("layouts", "true");
        if (this.exportOptions.queryReports) params.append("queryReports", "true");
        if (this.exportOptions.importMappings) params.append("importMappings", "true");

        const response = await wencesApi.get(`/api/database/export?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: "blob",
        });
        const blob = new Blob([response.data], { type: "application/json" });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.download = `export_${new Date()
          .toISOString()
          .replace(/[:.]/g, "-")}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        Swal.fire({
          title: "¡Éxito!",
          text: "Base de datos exportada exitosamente",
          icon: "success",
          timer: 3000,
          showConfirmButton: false,
        });
      } catch (error) {
        console.error("Error exporting database:", error);
        let errorMessage = "Error al exportar la base de datos";
        if (error.response?.data?.error)
          errorMessage = error.response.data.error;
        Swal.fire({ title: "Error", text: errorMessage, icon: "error" });
      } finally {
        this.loading = false;
      }
    },
    async importDatabase() {
      if (!this.importFile) {
        Swal.fire({
          title: "Error",
          text: "Por favor, selecciona un archivo JSON para importar",
          icon: "error",
        });
        return;
      }
      const result = await Swal.fire({
        title: "¿Estás seguro?",
        text: "Esta acción sobrescribirá los datos en la base de datos actual. Esta operación no se puede deshacer.",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sí, importar",
        cancelButtonText: "Cancelar",
      });
      if (!result.isConfirmed) {
        return;
      }
      this.loading = true;
      try {
        const token = localStorage.getItem("token");
        const formData = new FormData();
        formData.append("file", this.importFile);
        const response = await wencesApi.post(
          "/api/database/import",
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "multipart/form-data",
            },
          }
        );
        if (response.data.success) {
          Swal.fire({
            title: "¡Éxito!",
            text: "Base de datos importada exitosamente",
            icon: "success",
            timer: 3000,
            showConfirmButton: false,
          });
          this.importFile = null;
          this.$refs.importFileInput.value = "";
        } else {
          throw new Error(response.data.error || "Error en la importación");
        }
      } catch (error) {
        console.error("Error importing database:", error);
        let errorMessage = "Error al importar la base de datos";
        if (error.response?.data?.error)
          errorMessage = error.response.data.error;
        Swal.fire({ title: "Error", text: errorMessage, icon: "error" });
      } finally {
        this.loading = false;
      }
    },

    // Método para obtener zona horaria por defecto
    detectUserTimezone() {
      return "UTC+1"; // Por defecto para España
    },
    // Cargar opciones de moneda desde el listado 'currency'
    async loadCurrencyOptions() {
      this.loadingCurrencies = true;
      try {
        const token = localStorage.getItem("token");
        const response = await wencesApi.get("/api/gotrading/lists", {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Buscar el listado de tipo 'currency'
        const currencyList = response.data.find((list) => list.type === "currency");
        if (currencyList && currencyList.values) {
          this.currencyOptions = currencyList.values;
          this.currencyListId = currencyList._id;
          
          // Guardar el label de la moneda del sistema en localStorage
          this.updateSystemCurrencyInLocalStorage();
        } else {
          this.currencyOptions = [];
          this.currencyListId = null;
        }
      } catch (error) {
        console.error("Error loading currency options:", error);
        this.currencyOptions = [];
        this.currencyListId = null;
      } finally {
        this.loadingCurrencies = false;
      }
    },
    // Actualizar la info de la moneda del sistema en localStorage
    updateSystemCurrencyInLocalStorage() {
      if (this.settings.systemCurrency && this.currencyOptions.length) {
        const systemCurrency = this.currencyOptions.find(c => c._id === this.settings.systemCurrency);
        if (systemCurrency) {
          localStorage.setItem("systemCurrencyId", this.settings.systemCurrency);
          localStorage.setItem("systemCurrencyLabel", systemCurrency.label);
        }
      }
    },
    // Obtener el label de la moneda del sistema
    getSystemCurrencyLabel() {
      if (!this.settings.systemCurrency) return '';
      const currency = this.currencyOptions.find(c => c._id === this.settings.systemCurrency);
      return currency ? currency.label : '';
    },
    // Cuando cambia la moneda del sistema, recargar las tasas
    async onSystemCurrencyChange() {
      // Recargar las tasas después de guardar la configuración
      this.currentRates = [];
    },
    
    // Cargar tasas actuales desde el historial
    async loadCurrentRates() {
      if (!this.settings.systemCurrency) return;
      
      this.loadingRates = true;
      try {
        const token = localStorage.getItem("token");
        const response = await wencesApi.get("/api/gotrading/currency-rate-history/current-rates", {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success) {
          this.currentRates = response.data.data || [];
        }
      } catch (error) {
        console.error("Error loading current rates:", error);
        this.currentRates = [];
      } finally {
        this.loadingRates = false;
      }
    },
    
    // Obtener el valor de display de la tasa actual
    getCurrentRateDisplay(currencyId) {
      const rate = this.currentRates.find(r => r.currencyId === currencyId);
      if (!rate || rate.displayValue === null) return null;
      return rate.displayValue;
    },
    
    // Obtener la fecha de la tasa actual
    getCurrentRateDate(currencyId) {
      const rate = this.currentRates.find(r => r.currencyId === currencyId);
      return rate?.effectiveDate || null;
    },
    
    // Formatear fecha
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },
    
    // Formatear fecha y hora
    formatDateTime(date) {
      if (!date) return '';
      return new Date(date).toLocaleString('es-ES', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    // Abrir modal para agregar nueva tasa
    openAddRateModal(currency) {
      const now = new Date();
      // Formatear para input datetime-local (YYYY-MM-DDTHH:MM)
      const localDateTime = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      
      this.rateModal = {
        show: true,
        isEditing: false,
        recordId: null,
        currencyId: currency._id,
        currencyLabel: currency.label,
        displayValue: null,
        effectiveDate: localDateTime,
        notes: ''
      };
    },
    
    // Abrir modal de historial
    async openHistoryModal(currency) {
      this.historyModal = {
        show: true,
        currencyId: currency._id,
        currencyLabel: currency.label,
        records: []
      };
      
      await this.loadCurrencyHistory(currency._id);
    },
    
    // Cargar historial de una moneda
    async loadCurrencyHistory(currencyId) {
      this.loadingHistory = true;
      try {
        const token = localStorage.getItem("token");
        const response = await wencesApi.get(`/api/gotrading/currency-rate-history/currency/${currencyId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.data.success) {
          this.historyModal.records = response.data.data || [];
        }
      } catch (error) {
        console.error("Error loading currency history:", error);
        this.historyModal.records = [];
      } finally {
        this.loadingHistory = false;
      }
    },
    
    // Cerrar modal de tasa
    closeRateModal() {
      this.rateModal.show = false;
    },
    
    // Cerrar modal de historial
    closeHistoryModal() {
      this.historyModal.show = false;
    },
    
    // Editar un registro de tasa
    editRate(record) {
      // Formatear fecha para input datetime-local
      const date = new Date(record.effectiveDate);
      const localDateTime = new Date(date.getTime() - date.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);
      
      this.rateModal = {
        show: true,
        isEditing: true,
        recordId: record._id,
        currencyId: record.currencyId,
        currencyLabel: record.currencyLabel,
        displayValue: record.displayValue,
        effectiveDate: localDateTime,
        notes: record.notes || ''
      };
    },
    
    // Guardar tasa (crear o actualizar)
    async saveRate() {
      if (!this.rateModal.displayValue || this.rateModal.displayValue <= 0) {
        Swal.fire({
          title: "Error",
          text: "Por favor ingresa un valor de tasa válido",
          icon: "error"
        });
        return;
      }
      
      this.savingRate = true;
      try {
        const token = localStorage.getItem("token");
        
        const data = {
          currencyId: this.rateModal.currencyId,
          displayValue: this.rateModal.displayValue,
          effectiveDate: new Date(this.rateModal.effectiveDate).toISOString(),
          notes: this.rateModal.notes || null
        };
        
        if (this.rateModal.isEditing) {
          await wencesApi.put(`/api/gotrading/currency-rate-history/${this.rateModal.recordId}`, data, {
            headers: { Authorization: `Bearer ${token}` },
          });
        } else {
          await wencesApi.post("/api/gotrading/currency-rate-history", data, {
            headers: { Authorization: `Bearer ${token}` },
          });
        }
        
        // Cerrar modal y recargar datos
        this.closeRateModal();
        await this.loadCurrentRates();
        
        // Si el modal de historial está abierto, recargarlo
        if (this.historyModal.show && this.historyModal.currencyId === this.rateModal.currencyId) {
          await this.loadCurrencyHistory(this.historyModal.currencyId);
        }
        
        Swal.fire({
          title: "¡Éxito!",
          text: this.rateModal.isEditing 
            ? "Tasa actualizada exitosamente" 
            : "Nueva tasa guardada exitosamente",
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        console.error("Error saving rate:", error);
        Swal.fire({
          title: "Error",
          text: error.response?.data?.error || "Error al guardar la tasa",
          icon: "error"
        });
      } finally {
        this.savingRate = false;
      }
    },
    
    // Eliminar un registro de tasa
    async deleteRate(record) {
      const result = await Swal.fire({
        title: "¿Eliminar esta tasa?",
        text: `Se eliminará la tasa del ${this.formatDateTime(record.effectiveDate)}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#3085d6",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar"
      });
      
      if (!result.isConfirmed) return;
      
      try {
        const token = localStorage.getItem("token");
        await wencesApi.delete(`/api/gotrading/currency-rate-history/${record._id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        
        // Recargar datos
        await this.loadCurrentRates();
        if (this.historyModal.show) {
          await this.loadCurrencyHistory(this.historyModal.currencyId);
        }
        
        Swal.fire({
          title: "¡Eliminado!",
          text: "La tasa ha sido eliminada",
          icon: "success",
          timer: 2000,
          showConfirmButton: false
        });
      } catch (error) {
        console.error("Error deleting rate:", error);
        Swal.fire({
          title: "Error",
          text: "Error al eliminar la tasa",
          icon: "error"
        });
      }
    },
    async loadListTypes() {
      try {
        const token = localStorage.getItem("token");
        const response = await wencesApi.get("/api/gotrading/lists", {
          headers: { Authorization: `Bearer ${token}` },
        });
        // Extraer tipos únicos de listados
        const listTypesMap = new Map();
        response.data.forEach((list) => {
          if (list.type && list.name) {
            listTypesMap.set(list.type, {
              type: list.type,
              name: list.name,
            });
          }
        });
        this.availableListTypes = Array.from(listTypesMap.values()).sort((a, b) =>
          a.name.localeCompare(b.name)
        );
      } catch (error) {
        console.error("Error loading list types:", error);
        this.availableListTypes = [];
      }
    },
    toggleAllLists() {
      if (this.selectAllLists) {
        // Seleccionar todos los tipos de listados
        this.exportOptions.listTypes = this.availableListTypes.map(
          (lt) => lt.type
        );
      } else {
        // Deseleccionar todos
        this.exportOptions.listTypes = [];
      }
    },
  },
  computed: {
    externalAiChatUrl() {
      const base = getWencesApiBaseUrl().replace(/\/$/, "");
      return `${base}/api/external/ai/chat`;
    },
    registerTemplateDbNameTrimmed() {
      return (this.settings.registerTemplateDbName || "").trim();
    },
    registerRoleChoices() {
      const list = this.registerRoleOptions || [];
      return list.filter((r) => {
        if (!r || !r.name) return false;
        if (r.name === "admin") return false;
        if (Array.isArray(r.permissions) && r.permissions.includes("all_access")) return false;
        return true;
      });
    },
    hasExportOptionsSelected() {
      const hasOtherOptions = 
        this.exportOptions.models ||
        this.exportOptions.views ||
        this.exportOptions.referenceMappings ||
        this.exportOptions.adminLinks ||
        this.exportOptions.relationships ||
        this.exportOptions.layouts ||
        this.exportOptions.queryReports ||
        this.exportOptions.importMappings;
      const hasListTypes = this.exportOptions.listTypes.length > 0;
      return hasOtherOptions || hasListTypes;
    },
    selectAllLists: {
      get() {
        return (
          this.availableListTypes.length > 0 &&
          this.exportOptions.listTypes.length ===
            this.availableListTypes.length
        );
      },
      set(value) {
        // Este setter se maneja en toggleAllLists
      },
    },
    numberFormatOptions() {
      const formats = [];
      const decimals = ['0', '2', '3', '4'];
      const thousandsSeparators = [
        { value: 'none', symbol: '' },
        { value: 'dot', symbol: '.' },
        { value: 'comma', symbol: ',' }
      ];
      const decimalSeparators = [
        { value: 'dot', symbol: '.' },
        { value: 'comma', symbol: ',' }
      ];

      decimals.forEach(decimalsCount => {
        thousandsSeparators.forEach(thousands => {
          decimalSeparators.forEach(decimal => {
            // Skip invalid combinations (can't use same separator for both)
            if (thousands.value === decimal.value && thousands.value !== 'none') {
              return;
            }
            
            const formatValue = `${thousands.value}.${decimal.value}.${decimalsCount}`;
            const exampleNumber = 1000;
            const formatted = this.formatNumberExample(exampleNumber, formatValue);
            formats.push({
              value: formatValue,
              label: formatted
            });
          });
        });
      });

      return formats;
    },
  },
};
</script>

<style scoped>
/* Section title with toggle */
.section-title {
  cursor: pointer;
  display: flex;
  align-items: center;
  margin: 0;
  user-select: none;
}
.section-toggle {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  margin-right: 0.5rem;
}
.section-toggle i {
  font-size: 0.85rem;
  color: #6c757d;
}

/* Logo/image preview with remove button */
.logo-preview {
  width: 200px;
  height: 200px;
  overflow: visible;
}
.logo-preview img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}
.background-preview {
  width: 100%;
  max-width: 250px;
  height: 150px;
}
.remove-btn {
  position: absolute;
  top: -10px;
  right: -10px;
  width: 32px;
  height: 32px;
  border-radius: 50% !important;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 2px solid white;
  box-shadow: 0 2px 5px rgba(0,0,0,0.3);
  z-index: 10;
}
.remove-btn i {
  font-size: 1rem;
}
.remove-btn:hover {
  transform: scale(1.1);
}

/* Code textarea (CSS/JS) */
.code-textarea {
  font-family: "Courier New", Courier, monospace;
  min-height: 200px;
  resize: vertical;
  white-space: pre;
  tab-size: 2;
}

/* Currency Rates Section */
.currency-rates-section {
  margin-top: 1.5rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.subsection-title {
  color: #495057;
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
}

/* Currency Rates List */
.currency-rates-list {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.currency-rate-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 1rem;
  background: white;
  border-radius: 6px;
  border: 1px solid #e9ecef;
  flex-wrap: wrap;
}

.currency-rate-item.system-currency {
  background: #d4edda;
  border-color: #28a745;
}

.rate-equation {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-family: 'SF Mono', 'Monaco', 'Inconsolata', 'Roboto Mono', monospace;
  font-size: 0.95rem;
}

.rate-base {
  font-weight: 600;
  color: #495057;
  white-space: nowrap;
}

.rate-equals {
  color: #6c757d;
  font-weight: 500;
}

.rate-input {
  width: 120px !important;
  text-align: center !important;
  font-family: inherit !important;
  font-weight: 600 !important;
}

.rate-value-fixed {
  width: 120px;
  text-align: center;
  font-weight: 600;
  color: #28a745;
}

.rate-currency {
  font-weight: 600;
  color: #495057;
  white-space: nowrap;
}

.rate-current-value {
  min-width: 80px;
  font-weight: 600;
  color: #495057;
}

.rate-date {
  font-size: 0.8rem;
}

.rate-actions {
  display: flex;
  gap: 0.5rem;
  margin-left: auto;
}

/* Hide spinner for number inputs */
.rate-input::-webkit-outer-spin-button,
.rate-input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

.rate-input {
  -moz-appearance: textfield;
  appearance: textfield;
}

/* Modal overlay */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1050;
}

/* Rate modal */
.rate-modal,
.history-modal {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  max-width: 500px;
  width: 90%;
  max-height: 90vh;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.history-modal {
  max-width: 600px;
}

.modal-header-custom {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f8f9fa;
}

.modal-title {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
}

.btn-close-modal {
  background: none;
  border: none;
  font-size: 1.2rem;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem;
  line-height: 1;
}

.btn-close-modal:hover {
  color: #333;
}

.modal-body-custom {
  padding: 1.5rem;
  overflow-y: auto;
}

.modal-footer-custom {
  padding: 1rem 1.5rem;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  background: #f8f9fa;
}

.currency-info {
  text-align: center;
  padding: 0.75rem;
  background: #e9ecef;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.rate-input-group {
  margin-bottom: 1rem;
}

.rate-equation-input {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
  margin-top: 0.5rem;
}

.rate-equation-input .rate-input {
  width: 120px !important;
  text-align: center;
}

/* History list */
.history-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.history-item {
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.history-rate {
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

.history-meta {
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  color: #6c757d;
}

.history-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

@media (max-width: 576px) {
  .currency-rate-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
  .rate-actions {
    margin-left: 0;
    margin-top: 0.5rem;
  }
  .rate-equation-input {
    flex-direction: column;
    align-items: stretch;
  }
  .rate-equation-input .rate-input {
    width: 100% !important;
  }
}
.terms-editor {
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
  font-family: inherit;
  font-size: 1rem;
  line-height: 1.5;
  min-height: 400px;
  max-height: 600px;
  overflow-y: auto;
  background-color: #fff;
}

.terms-editor:focus {
  border-color: #80bdff;
  outline: 0;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
}

.terms-editor p {
  margin-bottom: 0.5rem;
}

.terms-editor h1,
.terms-editor h2,
.terms-editor h3,
.terms-editor h4,
.terms-editor h5,
.terms-editor h6 {
  margin-top: 1rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.terms-editor ul,
.terms-editor ol {
  margin-left: 1.5rem;
  margin-bottom: 0.5rem;
}

.terms-editor strong {
  font-weight: 600;
}

.terms-editor em {
  font-style: italic;
}
</style>
